"use client"

import React, { createContext, useContext, useEffect, useState } from "react"

import { FeatureFlagClient, FeatureFlagValue } from "./FeatureFlagClient"

interface FeatureFlagContextType {
  client: FeatureFlagClient | null
  isReady: boolean
}

export const FeatureFlagContext = createContext<FeatureFlagContextType>({
  client: null,
  isReady: false,
})

interface FeatureFlagProviderProps {
  client: FeatureFlagClient
  children: React.ReactNode
}

export function FeatureFlagProvider({
  client,
  children,
}: FeatureFlagProviderProps) {
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    const checkReady = () => {
      if (client.isReady()) {
        setIsReady(true)
      }
    }

    // Check if already ready
    checkReady()

    // Listen for flags loaded
    client.onFlagsLoaded(() => {
      setIsReady(true)
    })

    return () => {
      // Cleanup if needed
    }
  }, [client])

  return (
    <FeatureFlagContext.Provider value={{ client, isReady }}>
      {children}
    </FeatureFlagContext.Provider>
  )
}

/**
 * Hook to check if feature flags are loaded and ready
 */
export function useFeatureFlagsLoaded(): boolean {
  const { isReady } = useContext(FeatureFlagContext)
  return isReady
}

/**
 * Hook to check if a feature flag is enabled
 */
export function useFlag(flagKey: string): boolean {
  const { client, isReady } = useContext(FeatureFlagContext)

  if (!client || !isReady) {
    return false // Default to false when not ready
  }

  return client.isEnabled(flagKey)
}

/**
 * Hook to get the variant/value of a feature flag
 */
export function useVariant(flagKey: string): FeatureFlagValue {
  const { client, isReady } = useContext(FeatureFlagContext)

  if (!client || !isReady) {
    return false // Default value when not ready
  }

  return client.getVariant(flagKey)
}
